import { useEffect, useState } from 'react';
import { useReferralCookie } from 'contexts/useReferralCookie';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentUser } from 'customHooks/useCurrentUser';
import { logError } from 'helpers/errors';
import { useFlags } from 'launchdarkly-react-client-sdk';

interface GrowSurfParticipant {
    id: string;
    firstName: string;
    lastName: string;
    referralCount: number;
    monthlyReferralCount: number;
    shareUrl: string;
}

export const useGrowSurf = () => {
    const { growsurf: featureFlag = false } = useFlags();
    const [growSurfReady, setGrowSurfReady] = useState(false);
    const { email, growSurfHash } = useCurrentUser();
    const { referralLink } = useCurrentAccount();
    const { setReferralData } = useReferralCookie();

    const [growSurfId, setGrowSurfId] = useState<string | null>(null);

    useEffect(() => {
        if (!growSurfId || !referralLink) return;
        setReferralData({ growSurfId, referralUrl: referralLink });
    }, [growSurfId, referralLink, setReferralData]);

    useEffect(() => {
        if (!growSurfReady || !email || !growSurfHash) return;

        const getGrowSurfParticipant = (email: string): Promise<GrowSurfParticipant> => {
            return window.growsurf.getParticipantByEmail(email);
        };

        const handleParticipantInit = async () => {
            try {
                if (!email) return;
                const participant = await getGrowSurfParticipant(email);
                setGrowSurfId(participant?.id);
            } catch (e) {
                logError(e as Error);
            }
        };

        const initParticipant = async () => {
            try {
                await window.growsurf.init({
                    email,
                    hash: growSurfHash,
                    campaign: import.meta.env.VITE_DASHBOARD_PUBLIC_GROWSURF_CAMPAIGN_ID,
                });
            } catch (e) {
                logError(e as Error);
            }
        };

        window.addEventListener('grsfReady', handleParticipantInit);
        initParticipant();

        return () => {
            window.removeEventListener('grsfReady', handleParticipantInit);
        };
    }, [growSurfReady, email, growSurfHash]);

    useEffect(() => {
        const handleScriptReady = () => {
            window.removeEventListener('grsfReady', handleScriptReady);
            setGrowSurfReady(true);
        };
        if (window.growsurf) {
            handleScriptReady();
        } else {
            window.addEventListener('grsfReady', handleScriptReady);
        }
        return () => {
            window.removeEventListener('grsfReady', handleScriptReady);
        };
    }, []);

    useEffect(() => {
        const campaignId = import.meta.env.VITE_DASHBOARD_PUBLIC_GROWSURF_CAMPAIGN_ID;
        if (!featureFlag || !campaignId) return;

        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.async = true;
        script.src = 'https://app.growsurf.com/growsurf.js?v=2.0.0';

        script.setAttribute('grsf-campaign', campaignId);
        document.head.appendChild(script);
    }, [featureFlag]);

    if (growSurfReady) {
        return window.growsurf;
    }
    return null;
};
